import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { AlertService } from '../../../core/ui/alert';
import { LoaderService } from '../../../core/ui/loader';
import { Permission } from '../../../shared/models/permission';
import { Role } from '../../../shared/models/role';
import { FunctionResponse } from '../../../shared/models/response';
import { convertFirestoreTimestampToDate } from '../../../shared/utils/date-converter';
import { PermissionFormData } from '../../../shared/models/permission-form-data';
import { RoleFormData } from '../../../shared/models/role-form-data';
import { UserFormData } from '../../../shared/models/user-form-data';
import { User } from '../../../shared/models/user';
import { Entity } from '../../../shared/models/entity';
import { EntityFormData } from '../../../shared/models/entity-form-data';
import { Partner } from '../../../shared/models/partner';
import { PartnerFormData } from '../../../shared/models/partner-form-data';
import { Route } from '../../../shared/models/route';
import { RouteFormData } from '../../../shared/models/route-form-data';
import { Bus } from '../../../shared/models/bus';
import { BusFormData } from '../../../shared/models/bus-form-data';
import { GeneralSettingsData } from '../../../shared/models/general-settings';
import { GeneralSettingsFormData } from '../../../shared/models/general-settings-form-data';
import { Liquidation } from '../../../shared/models/liquidation';
import { LiquidationFormData } from '../../../shared/models/liquidation-form-data';
import { DiscountFormData } from '../../../shared/models/discount-form-data';
import { Discount } from '../../../shared/models/discount';
import { LiquidationReviewHeader } from '../../../shared/models/liquidation-review-header';
import { PartnerLiquidationReviewItem } from '../../../shared/models/partner-liquidation-review-item';
import { PartnerLiquidationDetail } from '../../../shared/models/partner-liquidation-detail';

@Injectable({
  	providedIn: 'root'
})

export class AdminService {
  	constructor(private functions: Functions, private alertService: AlertService, private loaderService: LoaderService) { }

	async createPermission(permissionData: PermissionFormData): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<typeof permissionData, FunctionResponse<any>>(this.functions, 'createPermission');
            const result   = await callable(permissionData);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al crear permiso:', error);

            this.alertService.error(error.message || 'Error desconocido al crear permiso.', 'Error');

            return false;
        }
    }

    async getAllPermissions(): Promise<Permission[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<Permission[]>>(this.functions, 'getAllPermissions');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const permissions = result.data.data.map(p => ({
                    ...p,
                    createdAt: convertFirestoreTimestampToDate(p.createdAt),
                    updatedAt: convertFirestoreTimestampToDate(p.updatedAt)
                }));

                return permissions;
            } else {
                this.alertService.error(result.data.message || 'No se pudieron obtener los permisos.', 'Error');

                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al obtener todos los permisos:', error);

            this.alertService.error(error.message || 'Error desconocido al obtener permisos.', 'Error');

            return null;
        }
    }

    async updatePermission(id: string, updateData: Partial<Omit<PermissionFormData, 'id'>>): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ id: string } & Partial<Omit<PermissionFormData, 'id'>>, FunctionResponse<any>>(this.functions, 'updatePermission');
            const result   = await callable({ id, ...updateData });

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al actualizar permiso:', error);

            this.alertService.error(error.message || 'Error desconocido al actualizar permiso.', 'Error');

            return false;
        }
    }

    async deletePermission(id: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ id: string }, FunctionResponse<any>>(this.functions, 'deletePermission');
            const result   = await callable({id});

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al eliminar permiso:', error);

            this.alertService.error(error.message || 'Error desconocido al eliminar permiso.', 'Error');

            return false;
        }
    }

    async createRole(roleData: RoleFormData): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<typeof roleData, FunctionResponse<any>>(this.functions, 'createRole');
            const result   = await callable(roleData);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al crear rol:', error);

            this.alertService.error(error.message || 'Error desconocido al crear rol.', 'Error');

            return false;
        }
    }

    async getAllRoles(): Promise<Role[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<Role[]>>(this.functions, 'getAllRoles');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const roles = result.data.data.map(r => ({
                    ...r,
                    createdAt: convertFirestoreTimestampToDate(r.createdAt),
                    updatedAt: convertFirestoreTimestampToDate(r.updatedAt)
                }));

                return roles;
            } else {
                this.alertService.error(result.data.message || 'No se pudieron obtener los roles.', 'Error');

                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al obtener todos los roles:', error);

            this.alertService.error(error.message || 'Error desconocido al obtener roles.', 'Error');

            return null;
        }
    }

    async updateRole(id: string, updateData: Partial<Omit<RoleFormData, 'id'>>): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ id: string } & Partial<Omit<RoleFormData, 'id'>>, FunctionResponse<any>>(this.functions, 'updateRole');
            const result   = await callable({ id, ...updateData });

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al actualizar rol:', error);

            this.alertService.error(error.message || 'Error desconocido al actualizar rol.', 'Error');

            return false;
        }
    }

    async deleteRole(id: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ id: string }, FunctionResponse<any>>(this.functions, 'deleteRole');
            const result   = await callable({ id });

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al eliminar rol:', error);

            this.alertService.error(error.message || 'Error desconocido al eliminar rol.', 'Error');

            return false;
        }
    }

    async getAllUsers(): Promise<User[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<User[]>>(this.functions, 'listUsers');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const users = result.data.data.map(u => ({
                    ...u,
                    creationTime: convertFirestoreTimestampToDate(u.creationTime),
                    lastSignInTime: u.lastSignInTime ? convertFirestoreTimestampToDate(u.lastSignInTime) : null
                }));

                return users;
            } else {
                this.alertService.error(result.data.message || 'No se pudieron obtener los usuarios.', 'Error');

                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al obtener todos los usuarios:', error);

            this.handleCallableError(error, 'obtener usuarios');

            return null;
        }
    }

    async createUser(userData: UserFormData): Promise<boolean> {
        this.loaderService.show();

        try {
            /*
            const dataToSend = {
                email       : userData.email,
                password    : userData.password,
                displayName : userData.displayName,
                disabled    : userData.disabled,
                roleIds     : userData.roleIds
            };
            */

            const callable = httpsCallable<typeof userData, FunctionResponse<any>>(this.functions, 'createUser');
            const result   = await callable(userData);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al crear usuario:', error);

            this.handleCallableError(error, 'crear usuario');

            return false;
        }
    }

    async updateUser(uid: string, updateData: Partial<Omit<UserFormData, 'uid' | 'email' | 'password'>> & { email?: string; password?: string }): Promise<boolean> {
        this.loaderService.show();

        try {
            const dataToSend: Partial<UserFormData> & { uid: string } = {
                uid: uid,
                ...updateData
            };

            const callable = httpsCallable<typeof dataToSend, FunctionResponse<any>>(this.functions, 'updateUser');
            const result   = await callable(dataToSend);

            this.loaderService.hide();

            if (result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;

            }
        } catch (error: any) {
            this.loaderService.hide();

            console.error('Error al actualizar usuario:', error);

            this.handleCallableError(error, 'actualizar usuario');

            return false;
        }
    }

    async deleteUser(uid: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ uid: string }, FunctionResponse<any>>(this.functions, 'deleteUser');
            const result   = await callable({ uid });

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch (error: any) {
            this.loaderService.hide();

            console.error('Error al eliminar usuario:', error);

            this.handleCallableError(error, 'eliminar usuario');

            return false;
        }
    }

    async createEntity(entityData: EntityFormData): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<typeof entityData, FunctionResponse<any>>(this.functions, 'createEntity');
            const result   = await callable(entityData);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error creating entity:', error);

            this.handleCallableError(error, 'crear entidad');

            return false;
        }
    }

    async getAllEntities(): Promise<Entity[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<Entity[]>>(this.functions, 'getAllEntities');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const entities = result.data.data.map(e => ({
                    ...e,
                    createdAt: convertFirestoreTimestampToDate(e.createdAt),
                    updatedAt: convertFirestoreTimestampToDate(e.updatedAt)
                }));

                return entities;
            } else {
                this.alertService.error(result.data.message || 'Could not retrieve entities.', 'Error');

                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error getting all entities:', error);

            this.handleCallableError(error, 'obtener entidades');

            return null;
        }
    }

    async updateEntity(id: string, updateData: Partial<Omit<EntityFormData, 'id'>>): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ id: string } & Partial<Omit<EntityFormData, 'id'>>, FunctionResponse<any>>(this.functions, 'updateEntity');
            const result   = await callable({ id, ...updateData });

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error updating entity:', error);

            this.handleCallableError(error, 'actualizar entidad');

            return false;
        }
    }

    async deleteEntity(id: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ id: string }, FunctionResponse<any>>(this.functions, 'deleteEntity');
            const result   = await callable({id});

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error deleting entity:', error);

            this.handleCallableError(error, 'eliminar entidad');

            return false;
        }
    }

    async createRoute(routeData: RouteFormData): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<typeof routeData, FunctionResponse<any>>(this.functions, 'createRoute');

            /*
            const dataToSend = {
                id     : routeData.id,
                name   : routeData.name,
                status : routeData.status
            };
            */

            const result = await callable(routeData);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error creating route:', error);

            this.handleCallableError(error, 'crear ruta');

            return false;
        }
    }

    async getAllRoutes(): Promise<Route[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<Route[]>>(this.functions, 'getAllRoutes');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const routes = result.data.data.map(r => ({
                    ...r,
                    createdAt: convertFirestoreTimestampToDate(r.createdAt),
                    updatedAt: convertFirestoreTimestampToDate(r.updatedAt)
                }));

                return routes;
            } else {
                this.alertService.error(result.data.message || 'Could not retrieve routes.', 'Error');

                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error getting all routes:', error);

            this.handleCallableError(error, 'obtener rutas');

            return null;
        }
    }

    async updateRoute(id: string, updateData: Partial<Omit<RouteFormData, 'id'>>): Promise<boolean> {
        this.loaderService.show();

        try {
            const dataToSend = {
                id     : id,
                name   : updateData.name,
                status : updateData.status
            };

            const callable = httpsCallable<typeof dataToSend, FunctionResponse<any>>(this.functions, 'updateRoute');
            const result   = await callable(dataToSend);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;

            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error updating route:', error);

            this.handleCallableError(error, 'actualizar ruta');

            return false;
        }
    }

    async deleteRoute(id: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ id: string }, FunctionResponse<any>>(this.functions, 'deleteRoute');
            const result   = await callable({id});

            this.loaderService.hide();


            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error deleting route:', error);

            this.handleCallableError(error, 'eliminar ruta');

            return false;
        }
    }

    async createPartner(partnerData: PartnerFormData): Promise<boolean> {
        this.loaderService.show();

        try {
            /*
            const dataToSend = {
                email         : partnerData.email,
                displayName   : partnerData.displayName || null,
                disabled      : partnerData.disabled ?? false,
                paysISR       : partnerData.paysISR,
                billingMode   : partnerData.billingMode,
                NIT           : partnerData.NIT,
                accountNumber : partnerData.accountNumber
            };
            */

            const callable = httpsCallable<typeof partnerData, FunctionResponse<any>>(this.functions, 'createPartner');
            const result   = await callable(partnerData);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error creating partner:', error);

            this.handleCallableError(error, 'crear cliente');

            return false;
        }
    }

    async getAllPartners(): Promise<Partner[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<Partner[]>>(this.functions, 'getAllPartners');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const partners = result.data.data.map(p => ({
                    ...p,
                    creationTime   : p.creationTime ? convertFirestoreTimestampToDate(p.creationTime) : null,
                    lastSignInTime : p.lastSignInTime ? convertFirestoreTimestampToDate(p.lastSignInTime) : null
                }));

                return partners;
            } else {
                this.alertService.error(result.data.message || 'No se pudieron obtener los clientes.', 'Error');

                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error getting all partners:', error);

            this.handleCallableError(error, 'obtener clientes');

            return null;
        }
    }

    async updatePartner(uid: string, updateData: Partial<Omit<PartnerFormData, 'uid'>>): Promise<boolean> {
        this.loaderService.show();

        try {
            const dataToSend: Partial<PartnerFormData> & { uid: string } = {
                uid           : uid,
                email         : updateData.email,
                displayName   : updateData.displayName,
                disabled      : updateData.disabled,
                paysISR       : updateData.paysISR,
                billingMode   : updateData.billingMode,
                notificationGroupingMode: updateData.notificationGroupingMode,
                NIT           : updateData.NIT,
                accountNumber : updateData.accountNumber
            };

            const callable = httpsCallable<typeof dataToSend, FunctionResponse<any>>(this.functions, 'updatePartner');
            const result   = await callable(dataToSend);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error updating partner:', error);

            this.handleCallableError(error, 'actualizar cliente');

            return false;
        }
    }

    async deletePartner(uid: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ uid: string }, FunctionResponse<any>>(this.functions, 'deletePartner');
            const result   = await callable({uid});

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error deleting partner:', error);

            this.handleCallableError(error, 'eliminar cliente');

            return false;
        }
    }

    async createBus(busData: BusFormData): Promise<boolean> {
        this.loaderService.show();

        try {
            /*
            const dataToSend = {
                id           : busData.id,
                name         : busData.name,
                licensePlate : busData.licensePlate,
                status       : busData.status,
                partnerId    : busData.partnerId,
                routeId      : busData.routeId,
                entityId     : busData.entityId
            };
            */

            const callable = httpsCallable<typeof busData, FunctionResponse<any>>(this.functions, 'createBus');
            const result   = await callable(busData);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error creating bus:', error);

            this.handleCallableError(error, 'crear bus');

            return false;
        }
    }

    async getAllBuses(): Promise<Bus[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<Bus[]>>(this.functions, 'getAllBuses');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const buses = result.data.data.map(b => ({
                    ...b,
                    createdAt : convertFirestoreTimestampToDate(b.createdAt),
                    updatedAt : convertFirestoreTimestampToDate(b.updatedAt)
                }));

                return buses;
            } else {
                this.alertService.error(result.data.message || 'No se pudieron obtener los buses.', 'Error');

                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error getting all buses:', error);

            this.handleCallableError(error, 'obtener buses');

            return null;
        }
    }

    async updateBus(id: string, updateData: Partial<Omit<BusFormData, 'id'>>): Promise<boolean> {
        this.loaderService.show();

        try {
            const dataToSend: Partial<BusFormData> & { id: string } = {
                id           : id,
                name         : updateData.name,
                licensePlate : updateData.licensePlate,
                status       : updateData.status,
                partnerId    : updateData.partnerId,
                routeId      : updateData.routeId,
                entityId     : updateData.entityId
            };

            const callable = httpsCallable<typeof dataToSend, FunctionResponse<any>>(this.functions, 'updateBus');
            const result   = await callable(dataToSend);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error updating bus:', error);

            this.handleCallableError(error, 'actualizar bus');

            return false;
        }
    }


    async deleteBus(id: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ id: string }, FunctionResponse<any>>(this.functions, 'deleteBus');
            const result   = await callable({id});

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error deleting bus:', error);

            this.handleCallableError(error, 'eliminar bus');

            return false;
        }
    }

    async getGeneralSettings(): Promise<GeneralSettingsData | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<GeneralSettingsData>>(this.functions, 'getGeneralSettings');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const settings = {
                    ...result.data.data,
                    createdAt: convertFirestoreTimestampToDate(result.data.data.createdAt),
                    updatedAt: convertFirestoreTimestampToDate(result.data.data.updatedAt)
                };

                return settings;
            } else {
                this.alertService.error(result.data.message || 'No se pudo obtener la configuración general.', 'Error');

                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al obtener la configuración general:', error);

            this.handleCallableError(error, 'obtener configuración general');

            return null;
        }
    }

    async updateGeneralSettings(settingsData: GeneralSettingsFormData): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<GeneralSettingsFormData, FunctionResponse<any>>(this.functions, 'updateGeneralSettings');
            const result   = await callable(settingsData);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al actualizar la configuración general:', error);

            this.handleCallableError(error, 'actualizar configuración general');

            return false;
        }
    }

    async createLiquidation(liquidationData: LiquidationFormData, showSuccessAlert: boolean = true): Promise<string | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<typeof liquidationData, FunctionResponse<string>>(this.functions, 'createLiquidation');
            const result   = await callable(liquidationData);

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                if (showSuccessAlert) {
                    this.alertService.success(result.data.message, 'Éxito');
                }

                return result.data.data;
            } else {
                this.alertService.error(result.data.message || 'No se pudo crear la liquidación.', 'Error');

                return null;
            }
        } catch (error: any) {
            this.loaderService.hide();

            console.error('Error al crear liquidación:', error);

            this.handleCallableError(error, 'crear liquidación');

            return null;
        }
    }

    async getLiquidation(id: string): Promise<Liquidation | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ id: string }, FunctionResponse<Liquidation>>(this.functions, 'getLiquidation');
            const result   = await callable({ id });

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const liquidation = {
                    ...result.data.data,
                    startDate: convertFirestoreTimestampToDate(result.data.data.startDate),
                    endDate: convertFirestoreTimestampToDate(result.data.data.endDate),
                    createdAt: convertFirestoreTimestampToDate(result.data.data.createdAt),
                    updatedAt: convertFirestoreTimestampToDate(result.data.data.updatedAt)
                };

                return liquidation;
            } else {
                this.alertService.error(result.data.message || 'No se pudo obtener la liquidación.', 'Error');

                return null;
            }
        } catch (error: any) {
            this.loaderService.hide();

            console.error('Error al obtener liquidación:', error);

            this.handleCallableError(error, 'obtener liquidación');

            return null;
        }
    }

    async getAllLiquidations(): Promise<Liquidation[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<Liquidation[]>>(this.functions, 'getAllLiquidations');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const liquidations = result.data.data.map(l => ({
                    ...l,
                    startDate: convertFirestoreTimestampToDate(l.startDate),
                    endDate: convertFirestoreTimestampToDate(l.endDate),
                    createdAt: convertFirestoreTimestampToDate(l.createdAt),
                    updatedAt: convertFirestoreTimestampToDate(l.updatedAt)
                }));

                return liquidations;
            } else {
                this.alertService.error(result.data.message || 'No se pudieron obtener las liquidaciones.', 'Error');

                return null;
            }
        } catch (error: any) {
            this.loaderService.hide();

            console.error('Error al obtener todas las liquidaciones:', error);

            this.handleCallableError(error, 'obtener todas las liquidaciones');

            return null;
        }
    }

    async updateLiquidation(id: string, updateData: Partial<LiquidationFormData>, showSuccessAlert: boolean = true): Promise<boolean> {
        this.loaderService.show();

        try {
            const dataToSend: Partial<LiquidationFormData> & { id: string } = { id, ...updateData };

            const callable = httpsCallable<typeof dataToSend, FunctionResponse<any>>(this.functions, 'updateLiquidation');
            const result   = await callable(dataToSend);

            this.loaderService.hide();

            if(result.data.success) {
                if (showSuccessAlert) {
                    this.alertService.success(result.data.message, 'Éxito');
                }

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al actualizar liquidación:', error);

            this.handleCallableError(error, 'actualizar liquidación');

            return false;
        }
    }

    async deleteLiquidation(id: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ id: string }, FunctionResponse<any>>(this.functions, 'deleteLiquidation');
            const result   = await callable({ id });

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al eliminar liquidación:', error);

            this.handleCallableError(error, 'eliminar liquidación');

            return false;
        }
    }

    async cloneLastLiquidation(): Promise<string | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<string>>(this.functions, 'cloneLastLiquidation');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                this.alertService.success(result.data.message, 'Éxito');

                return result.data.data;
            } else {
                this.alertService.error(result.data.message || 'La clonación falló o no se encontró la última liquidación.', 'Error');

                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al clonar liquidación:', error);

            this.handleCallableError(error, 'clonar liquidación');

            return null;
        }
    }

    async createDiscount(discountData: DiscountFormData): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<DiscountFormData, FunctionResponse<string>>(this.functions, 'createDiscount');
            const result   = await callable(discountData);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch (error: any) {
            this.loaderService.hide();
            this.handleCallableError(error, 'crear descuento');

            return false;
        }
    }

    async getAllDiscounts(): Promise<Discount[]> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<Discount[]>>(this.functions, 'getAllDiscounts');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const discounts = result.data.data.map(d => ({
                    ...d,
                    createdAt: convertFirestoreTimestampToDate(d.createdAt),
                    updatedAt: convertFirestoreTimestampToDate(d.updatedAt)
                }));

                return discounts;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return [];
            }
        } catch(error: any) {
            this.loaderService.hide();

            this.handleCallableError(error, 'obtener descuentos');

            return [];
        }
    }

    async updateDiscount(discountData: DiscountFormData): Promise<boolean> {
        this.loaderService.show();

        try {
            if(!discountData.id) {
                this.loaderService.hide();

                this.alertService.error('El ID del descuento es requerido para la actualización.', 'Error');

                return false;
            }

            const callable = httpsCallable<DiscountFormData, FunctionResponse<any>>(this.functions, 'updateDiscount');
            const result   = await callable(discountData);

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();
            this.handleCallableError(error, 'actualizar descuento');

            return false;
        }
    }

    async deleteDiscount(id: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ id: string }, FunctionResponse<any>>(this.functions, 'deleteDiscount');
            const result   = await callable({ id });

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message, 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();
            this.handleCallableError(error, 'eliminar descuento');

            return false;
        }
    }

    async reprocessLiquidationExcel(liquidationId: string): Promise<boolean> {
        try {
            const callable = httpsCallable<{ liquidationId: string }, FunctionResponse<any>>(this.functions, 'reprocessExistingExcel');
            const result   = await callable({ liquidationId });

            if(result.data.success) {
                return true;
            } else {
                return false;
            }
        } catch(error: any) {
            console.error('Error al reprocesar liquidación:', error);

            return false;
        }
    }

    async sendBulkWelcomeEmails(): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<any>>(this.functions, 'sendBulkWelcomeEmails');
            const result   = await callable();

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message || 'No se pudo enviar el correo masivo.', 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error sending bulk welcome emails:', error);

            this.handleCallableError(error, 'enviar correos masivos');

            return false;
        }
    }

    async sendPartnerWelcomeEmail(partnerUid: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ partnerUid: string }, FunctionResponse<any>>(this.functions, 'sendPartnerWelcomeEmail');
            const result   = await callable({ partnerUid });

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');

                return true;
            } else {
                this.alertService.error(result.data.message || 'No se pudo enviar el correo de bienvenida.', 'Error');

                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error sending partner welcome email:', error);

            this.handleCallableError(error, 'enviar correo de bienvenida');

            return false;
        }
    }

    async getPartnerLiquidationsForReview(liquidationId: string): Promise<{ header: LiquidationReviewHeader; items: PartnerLiquidationReviewItem[] } | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ liquidationId: string }, FunctionResponse<{ header: LiquidationReviewHeader; items: PartnerLiquidationReviewItem[] }>>(this.functions, 'getPartnerLiquidationsForReview');
            const result   = await callable({ liquidationId });

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const header = result.data.data.header;
                const items  = (result.data.data.items || []).map(item => ({
                    ...item,
                    createdAt: convertFirestoreTimestampToDate(item.createdAt)
                }));

                return { header, items };
            } else {
                this.alertService.error(result.data.message || 'No se pudieron obtener las liquidaciones de clientes.', 'Error');

                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al obtener liquidaciones de clientes para revisión:', error);

            this.alertService.error(error.message || 'Error desconocido al obtener liquidaciones de clientes.', 'Error');

            return null;
        }
    }

    async getPartnerLiquidationDetailForReview(liquidationId: string, partnerId: string): Promise<{ partnerName: string; detail: PartnerLiquidationDetail } | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ liquidationId: string; partnerId: string }, FunctionResponse<{ partnerName: string; detail: PartnerLiquidationDetail }>>(this.functions, 'getPartnerLiquidationDetailForReview');
            const result   = await callable({ liquidationId, partnerId });

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const detail = result.data.data.detail;
                return {
                    partnerName: result.data.data.partnerName,
                    detail: {
                        ...detail,
                        createdAt: convertFirestoreTimestampToDate(detail.createdAt),
                        invoices : Array.isArray(detail.invoices) ? detail.invoices.map(inv => ({
                            ...inv,
                            date: inv.date ? convertFirestoreTimestampToDate(inv.date as any) : inv.date
                        })) : [],
                        deposits : Array.isArray(detail.deposits) ? detail.deposits.map(dep => ({
                            ...dep,
                            date: dep.date ? convertFirestoreTimestampToDate(dep.date as any) : dep.date
                        })) : []
                    }
                };
            } else {
                this.alertService.error(result.data.message || 'No se pudo obtener el detalle de la liquidación.', 'Error');
                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al obtener detalle de liquidación de cliente:', error);

            this.alertService.error(error.message || 'Error desconocido al obtener detalle de liquidación de cliente.', 'Error');

            return null;
        }
    }

    async reviewPartnerLiquidation(liquidationId: string, partnerId: string, status: 'APPROVED' | 'REJECTED', rejectionReason?: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ liquidationId: string; partnerId: string; status: string; rejectionReason?: string }, FunctionResponse<any>>(this.functions, 'reviewPartnerLiquidation');
            const result   = await callable({ liquidationId, partnerId, status, rejectionReason });

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message, 'Éxito');
                return true;
            } else {
                this.alertService.error(result.data.message || 'No se pudo actualizar la liquidación del cliente.', 'Error');
                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al actualizar liquidación del cliente:', error);
            this.alertService.error(error.message || 'Error desconocido al actualizar liquidación del cliente.', 'Error');

            return false;
        }
    }

    async sendLiquidationNotifications(liquidationId: string): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ liquidationId: string }, FunctionResponse<any>>(this.functions, 'sendLiquidationNotifications');
            const result   = await callable({ liquidationId });

            this.loaderService.hide();

            if(result.data.success) {
                this.alertService.success(result.data.message || 'Notificaciones enviadas.', 'Éxito');
                return true;
            } else {
                this.alertService.error(result.data.message || 'No se pudieron enviar las notificaciones.', 'Error');
                return false;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al enviar notificaciones de liquidación:', error);
            this.handleCallableError(error, 'enviar notificaciones de liquidación');

            return false;
        }
    }

    async generateNotificationsReportExcel(liquidationId: string): Promise<any[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ liquidationId: string }, FunctionResponse<any>>(this.functions, 'generateNotificationsReportExcel');
            const result   = await callable({ liquidationId });

            this.loaderService.hide();

            if(result.data.success && Array.isArray(result.data.data)) {
                if(result.data.data.length === 0) {
                    this.alertService.warning(result.data.message || 'No se encontraron datos para generar el reporte.', 'Sin datos');
                    return result.data.data;
                }
                this.alertService.success(result.data.message || 'Reporte generado.', 'Éxito');
                return result.data.data;
            } else {
                this.alertService.error(result.data.message || 'No se pudo generar el reporte.', 'Error');
                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al generar reporte de notificaciones:', error);
            this.handleCallableError(error, 'generar el reporte de notificaciones');

            return null;
        }
    }

    async generateDiscountReportExcel(payload: { discountId: string; grouping: string; startDate: Date; endDate: Date; filterType?: string; filterIds?: string[] }): Promise<any[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ discountId: string; grouping: string; startDate: Date; endDate: Date; filterType?: string; filterIds?: string[] }, FunctionResponse<any>>(this.functions, 'generateDiscountReportExcel');
            const result   = await callable(payload);

            this.loaderService.hide();

            if(result.data.success && Array.isArray(result.data.data)) {
                if(result.data.data.length === 0) {
                    this.alertService.warning(result.data.message || 'No se encontraron datos para generar el reporte.', 'Sin datos');
                    return result.data.data;
                }
                this.alertService.success(result.data.message || 'Reporte generado.', 'Éxito');
                return result.data.data;
            } else {
                this.alertService.error(result.data.message || 'No se pudo generar el reporte.', 'Error');
                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al generar reporte de descuentos:', error);
            this.handleCallableError(error, 'generar el reporte de descuentos');

            return null;
        }
    }

    async generateDepositReportExcel(payload: { grouping: string; startDate: Date; endDate: Date; filterType?: string; filterIds?: string[] }): Promise<any[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ grouping: string; startDate: Date; endDate: Date; filterType?: string; filterIds?: string[] }, FunctionResponse<any>>(this.functions, 'generateDepositReportExcel');
            const result   = await callable(payload);

            this.loaderService.hide();

            if(result.data.success && Array.isArray(result.data.data)) {
                if(result.data.data.length === 0) {
                    this.alertService.warning(result.data.message || 'No se encontraron datos para generar el reporte.', 'Sin datos');
                    return result.data.data;
                }
                this.alertService.success(result.data.message || 'Reporte generado.', 'Éxito');
                return result.data.data;
            } else {
                this.alertService.error(result.data.message || 'No se pudo generar el reporte.', 'Error');
                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al generar reporte de depósitos:', error);
            this.handleCallableError(error, 'generar el reporte de depósitos');

            return null;
        }
    }

    async generateLiquidationPaymentReportExcel(payload: { grouping: string; startDate: Date; endDate: Date; filterType?: string; filterIds?: string[] }): Promise<any | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ grouping: string; startDate: Date; endDate: Date; filterType?: string; filterIds?: string[] }, FunctionResponse<any>>(this.functions, 'generateLiquidationPaymentReportExcel');
            const result   = await callable(payload);

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const reportRows = Array.isArray(result.data.data?.rows) ? result.data.data.rows : [];
                if(reportRows.length === 0) {
                    this.alertService.warning(result.data.message || 'No se encontraron datos para generar el reporte.', 'Sin datos');
                    return result.data.data;
                }
                this.alertService.success(result.data.message || 'Reporte generado.', 'Éxito');
                return result.data.data;
            } else {
                this.alertService.error(result.data.message || 'No se pudo generar el reporte.', 'Error');
                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al generar reporte pago de liquidación:', error);
            this.handleCallableError(error, 'generar el reporte pago de liquidación');

            return null;
        }
    }

    async generateMissingDocumentsReportExcel(payload: { grouping: string; startDate: Date; endDate: Date; filterType?: string; filterIds?: string[] }): Promise<any | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ grouping: string; startDate: Date; endDate: Date; filterType?: string; filterIds?: string[] }, FunctionResponse<any>>(this.functions, 'generateMissingDocumentsReportExcel');
            const result   = await callable(payload);

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const reportBlocks = Array.isArray(result.data.data?.blocks) ? result.data.data.blocks : [];
                if(reportBlocks.length === 0) {
                    this.alertService.warning(result.data.message || 'No se encontraron datos para generar el reporte.', 'Sin datos');
                    return result.data.data;
                }
                this.alertService.success(result.data.message || 'Reporte generado.', 'Éxito');
                return result.data.data;
            } else {
                this.alertService.error(result.data.message || 'No se pudo generar el reporte.', 'Error');
                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al generar reporte de documentos faltantes:', error);
            this.handleCallableError(error, 'generar el reporte de documentos faltantes');

            return null;
        }
    }

    async generateRouteAccountStatementReportExcel(payload: { routeId: string; year: number }): Promise<any | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ routeId: string; year: number }, FunctionResponse<any>>(this.functions, 'generateRouteAccountStatementReportExcel');
            const result   = await callable(payload);

            this.loaderService.hide();

            if(result.data.success && result.data.data) {
                const reportRoutes = Array.isArray(result.data.data?.routes) ? result.data.data.routes : [];
                if(reportRoutes.length === 0) {
                    this.alertService.warning(result.data.message || 'No se encontraron datos para generar el reporte.', 'Sin datos');
                    return result.data.data;
                }
                this.alertService.success(result.data.message || 'Reporte generado.', 'Éxito');
                return result.data.data;
            } else {
                this.alertService.error(result.data.message || 'No se pudo generar el reporte.', 'Error');
                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al generar reporte de estado de cuenta por ruta:', error);
            this.handleCallableError(error, 'generar el reporte de estado de cuenta por ruta');

            return null;
        }
    }

    async generateCatalogSnapshotReportExcel(payload: {
        reportType: string;
        grouping: string;
        startDate?: Date | null;
        endDate?: Date | null;
        filterType?: string;
        filterIds?: string[];
    }): Promise<any[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{
                reportType: string;
                grouping: string;
                startDate?: Date | null;
                endDate?: Date | null;
                filterType?: string;
                filterIds?: string[];
            }, FunctionResponse<any>>(this.functions, 'generateCatalogSnapshotReportExcel');
            const result   = await callable(payload);

            this.loaderService.hide();

            if(result.data.success && Array.isArray(result.data.data)) {
                if(result.data.data.length === 0) {
                    this.alertService.warning(result.data.message || 'No se encontraron datos para generar el reporte.', 'Sin datos');
                    return result.data.data;
                }
                this.alertService.success(result.data.message || 'Reporte generado.', 'Éxito');
                return result.data.data;
            } else {
                this.alertService.error(result.data.message || 'No se pudo generar el reporte.', 'Error');
                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al generar reporte de clientes:', error);
            this.handleCallableError(error, 'generar el reporte de clientes');

            return null;
        }
    }

    async generateBusLifecycleReportExcel(payload: {
        reportType: string;
        grouping: string;
        startDate?: Date | null;
        endDate?: Date | null;
        filterType?: string;
        filterIds?: string[];
    }): Promise<any[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{
                reportType: string;
                grouping: string;
                startDate?: Date | null;
                endDate?: Date | null;
                filterType?: string;
                filterIds?: string[];
            }, FunctionResponse<any>>(this.functions, 'generateBusLifecycleReportExcel');
            const result   = await callable(payload);

            this.loaderService.hide();

            if(result.data.success && Array.isArray(result.data.data)) {
                if(result.data.data.length === 0) {
                    this.alertService.warning(result.data.message || 'No se encontraron datos para generar el reporte.', 'Sin datos');
                    return result.data.data;
                }
                this.alertService.success(result.data.message || 'Reporte generado.', 'Éxito');
                return result.data.data;
            } else {
                this.alertService.error(result.data.message || 'No se pudo generar el reporte.', 'Error');
                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al generar reporte de autobuses:', error);
            this.handleCallableError(error, 'generar el reporte de autobuses');

            return null;
        }
    }

    async generateLiquidationSummaryExcel(
        liquidationId: string,
        grouping: 'partner' | 'entity' | 'route' | 'bus' | 'partner_entity_route',
        reportType: 'integrated' | 'bank_file' | 'invoice_reconciliation' | 'liquidation_by_route'
    ): Promise<any[] | null> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<{ liquidationId: string, grouping: string, reportType: string }, FunctionResponse<any>>(this.functions, 'generateLiquidationSummaryExcel');
            const result   = await callable({ liquidationId, grouping, reportType });

            this.loaderService.hide();

            if(result.data.success && Array.isArray(result.data.data)) {
                this.alertService.success(result.data.message || 'Resumen generado.', 'Éxito');
                return result.data.data;
            } else {
                this.alertService.error(result.data.message || 'No se pudo generar el resumen.', 'Error');
                return null;
            }
        } catch(error: any) {
            this.loaderService.hide();

            console.error('Error al generar resumen de liquidación:', error);
            this.handleCallableError(error, 'generar el resumen de liquidación');

            return null;
        }
    }

    private handleCallableError(error: any, operationName: string): void {
        let errorMessage = `Ocurrió un error al ${operationName}.`;

        if(error && typeof error.code === 'string') {
            switch (error.code) {
                case 'invalid-argument':
                    errorMessage = error.message || `Los datos proporcionados para ${operationName} no son válidos.`;
                break;
                case 'unauthenticated':
                    errorMessage = error.message || 'No estás autenticado. Por favor, inicia sesión de nuevo.';
                break;
                case 'permission-denied':
                    errorMessage = error.message || 'Acceso denegado. No tienes permisos para realizar esta acción.';
                break;
                case 'not-found':
                    errorMessage = error.message || `El elemento solicitado para ${operationName} no fue encontrado.`;
                break;
                case 'already-exists':
                    errorMessage = error.message || `El elemento que intentas crear al ${operationName} ya existe.`;
                break;
                case 'internal':
                    errorMessage = error.message || `Ocurrió un error interno en el servidor al ${operationName}. Por favor, inténtalo de nuevo o contacta a soporte.`;
                break;
                default:
                    errorMessage = error.message || `Ocurrió un error inesperado al ${operationName}.`;
                break;
            }
        } else {
            errorMessage = `Problemas de conexión o error desconocido al ${operationName}. Por favor, verifica tu conexión a internet o inténtalo de nuevo.`;
        }

        this.alertService.error(errorMessage, 'Error');
    }

    // TEMPORAL: corrección puntual de fechas — eliminar este método después de ejecutarlo una sola vez
    async fixLiquidationDateRangesTemp(): Promise<boolean> {
        this.loaderService.show();

        try {
            const callable = httpsCallable<void, FunctionResponse<any>>(this.functions, 'fixLiquidationDateRangesTemp');
            const result   = await callable();

            this.loaderService.hide();

            if (result.data.success) {
                console.log('Detalle de la corrección de fechas:', result.data.data);
                this.alertService.success(result.data.message || 'Fechas corregidas.', 'Éxito');
                return true;
            } else {
                this.alertService.error(result.data.message || 'No se pudieron corregir las fechas.', 'Error');
                return false;
            }
        } catch (error: any) {
            this.loaderService.hide();

            console.error('Error al corregir fechas de liquidaciones:', error);
            this.handleCallableError(error, 'corregir fechas de liquidaciones');

            return false;
        }
    }
}
